/**
 * Persistent banner for Meta connection status
 * Displays when connection is expired or has errors
 */

import { AlertTriangle, X, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useMetaConnectionStatus } from '@/context/MetaConnectionContext';
import { cn } from '@/lib/utils';

export function MetaConnectionBanner() {
  const { error, showBanner, dismissBanner } = useMetaConnectionStatus();

  if (!showBanner || !error) {
    return null;
  }

  return (
    <div
      className={cn(
        'w-full px-4 py-3',
        'bg-warning/10 border-b border-warning/30',
        'flex items-center justify-between gap-4'
      )}
      role="alert"
    >
      <div className="flex items-center gap-3">
        <AlertTriangle className="h-5 w-5 text-warning flex-shrink-0" />
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-primary">
            {error.message}
          </span>
          {error.code && (
            <span className="text-xs text-muted">
              (Error {error.code.replace('META_', '').replace(/_/g, ' ')})
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {error.recoveryAction === 'reconnect' && (
          <Link
            to="/team/settings"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md',
              'text-sm font-medium',
              'bg-warning text-white hover:bg-warning/90',
              'transition-colors'
            )}
          >
            Reconnect
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        )}
        <button
          onClick={dismissBanner}
          className={cn(
            'p-1.5 rounded-md',
            'text-muted hover:text-primary hover:bg-surface-subtle',
            'transition-colors'
          )}
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
